import {queries} from '@/shared/integrations/zero'
import {Stock} from '@cypro/engine/schema'
import {type GetPageQuery, type GetSingleQuery} from '@cypro/ui/utils/zero-virtualizer'
import {useCallback, useMemo, useState} from 'react'

export type StockStartRow = Pick<
  Stock,
  'id' | 'createdAt' | 'name' | 'earliestExpiryDate' | 'totalQuantity'
>

export function useGetInfiniteStocks({
  sort = {dir: 'desc', field: 'createdAt'},
  filters,
  limit = 25,
}: Omit<Partial<Parameters<typeof queries.stocks.listStocks>[0]>, 'cursor'> = {}) {
  const [search, setSearch] = useState('')

  const listContextParams = useMemo(
        () => ({sort, filters, limit, search}),
        // oxlint-disable-next-line react-hooks/exhaustive-deps
        [
          sort?.dir,
          sort?.field,
          filters?.search,
          filters?.types,
          filters?.categories,
          filters?.subcategories,
          filters?.suppliers,
          filters?.stockLocations,
          filters?.status,
          limit,
          search,
        ]
      )

  // @ts-expect-error schema type mismatch (works at runtime)
  const getPageQuery: GetPageQuery<Stock, StockStartRow> = useCallback(
    ({limit: pageLimit, start, dir}) => {
      const cursor =
        start?.id && start?.createdAt
          ? {
              id: start.id,
              direction: dir,
              createdAt: start.createdAt,
              name: start.name,
              earliestExpiryDate: start.earliestExpiryDate ?? undefined,
              totalQuantity: start.totalQuantity ?? undefined,
            }
          : undefined

      return {
        query: queries.stocks.listStocks({
          cursor,
          limit: pageLimit,
          sort,
          filters: {...filters, ...(search ? {search} : {})},
        }),
      }
    },
    // oxlint-disable-next-line react-hooks/exhaustive-deps
    [listContextParams]
  )

  // @ts-expect-error schema type mismatch (works at runtime)
  const getSingleQuery: GetSingleQuery<Stock> = useCallback(
    ({id}) => ({
      query: queries.stocks.getStockById(id),
    }),
    []
  )

  const toStartRow = useCallback(
    (row: Stock): StockStartRow => ({
      id: row.id,
      createdAt: row.createdAt,
      name: row.name,
      earliestExpiryDate: row.earliestExpiryDate,
      totalQuantity: row.totalQuantity,
    }),
    []
  )

  return {
    search,
    onSearchChange: setSearch,
    getPageQuery,
    getSingleQuery,
    toStartRow,
    listContextParams,
  }
}
